<!-- src/views/fatigue/AdminFatigueManageFormOptionsView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <!-- PREMIUM HEADER -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 no-print">
      <div class="bg-[#0A1630] rounded-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5">
          <ShieldCheckIcon class="h-32 w-32 text-white" />
        </div>
        
        <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Form Configuration</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-3">Dropdown Logic & Global Overrides</p>
          </div>
          
          <router-link :to="{ name: 'FatigueDashboard' }" class="bg-white/5 border border-white/10 text-white px-6 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2">
            <ArrowUturnLeftIcon class="h-4 w-4 text-[#20FCF1]" /> <span>Return to Fatigue</span>
          </router-link>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <svg class="animate-spin h-10 w-10 text-[#20FCF1]" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
        <p class="micro-label mt-4">Hydrating Configuration Data...</p>
      </div>

      <div v-else-if="error" class="bg-rose-50 border border-rose-100 p-8 rounded-[32px] text-center">
        <p class="text-rose-600 font-bold mb-4">{{ error }}</p>
        <button @click="fetchFormOptions" class="bg-[#0A1630] text-white px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest">Retry Connection</button>
      </div>
      
      <div v-else class="space-y-12">
        <!-- Section Containers -->
        <div v-for="section in formSections" :key="section.title" class="bg-white rounded-[40px] border border-slate-100 shadow-xl overflow-hidden">
          <div class="bg-slate-50 border-b border-slate-100 p-8">
            <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight">{{ section.title }}</h2>
          </div>
          
          <div class="p-8 space-y-16">
            <div v-for="optionGroupId in section.optionGroupIds" :key="optionGroupId">
              <div v-if="rawOptionsData[optionGroupId]">
                
                <!-- Group Header -->
                <div class="flex justify-between items-end mb-6 border-b border-slate-100 pb-4">
                  <div>
                    <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-widest">{{ formatTitle(optionGroupId) }}</h3>
                    <p class="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Control Group ID: {{ optionGroupId }}</p>
                  </div>
                  <div v-if="clientHasOverrides[optionGroupId]" class="flex items-center gap-2">
                    <span class="text-[10px] font-black text-[#20FCF1] bg-[#EBFDF8] px-3 py-1 rounded-full uppercase tracking-widest">Client Specific</span>
                    <button @click="revertToGlobal(optionGroupId)" class="text-[10px] font-black text-rose-500 hover:underline uppercase tracking-widest">Reset</button>
                  </div>
                  <span v-else class="text-[10px] font-black text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-widest">Global Default</span>
                </div>

                <!-- Option List -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  <div v-for="(option, index) in displayOptions[optionGroupId]" :key="index" class="group flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-[#20FCF1] transition-all">
                    <div class="flex flex-col">
                      <span class="text-[9px] font-mono text-slate-400 uppercase tracking-tighter">{{ option.value }}</span>
                      <span class="text-xs font-bold text-[#0A1630] uppercase tracking-tight">{{ option.text }}</span>
                    </div>
                    <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button @click="openEditModal(optionGroupId, option)" class="p-2 text-slate-400 hover:text-[#0A1630] transition-colors">
                        <PencilSquareIcon class="h-4 w-4" />
                      </button>
                      <button @click="handleDeleteOption(optionGroupId, option)" class="p-2 text-slate-400 hover:text-rose-500 transition-colors">
                        <TrashIcon class="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div v-if="!displayOptions[optionGroupId]?.length" class="col-span-full py-8 text-center border-2 border-dashed border-slate-100 rounded-2xl">
                    <p class="micro-label text-slate-300">No entries defined in this protocol</p>
                  </div>
                </div>

                <!-- Premium Inline Add Form -->
                <div class="bg-slate-900 rounded-[32px] p-6 shadow-2xl">
                  <form @submit.prevent="handleAddOption(optionGroupId)" class="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label class="block text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-2 ml-2">Internal Value</label>
                      <input type="text" v-model="newOptions[optionGroupId].value" placeholder="e.g. shift_rotation" required class="premium-dark-input" />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em] mb-2 ml-2">Display Text</label>
                      <input type="text" v-model="newOptions[optionGroupId].text" placeholder="e.g. Shift Rotation" required class="premium-dark-input" />
                    </div>
                    <div class="flex items-end">
                      <button type="submit" :disabled="isProcessing" class="w-full h-[52px] bg-[#20FCF1] text-[#0A1630] rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50">
                        {{ isProcessing ? 'Processing...' : 'Add To Protocol' }}
                      </button>
                    </div>
                  </form>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Modal -->
    <TransitionRoot as="template" :show="isEditModalOpen">
      <Dialog as="div" class="relative z-50" @close="closeModal">
        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
          <div class="fixed inset-0 bg-[#0A1630]/80 transition-opacity" />
        </TransitionChild>
        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <DialogPanel class="relative transform overflow-hidden rounded-[40px] bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <form @submit.prevent="handleUpdateOption">
                  <div class="bg-white p-10">
                    <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight mb-8">Refine Option</h3>
                    <div class="space-y-6">
                       <div>
                          <label class="micro-label mb-2">Internal Reference (Locked)</label>
                          <input type="text" :value="editingOption.current.value" disabled class="premium-input bg-slate-50 text-slate-400" />
                      </div>
                      <div>
                          <label class="micro-label mb-2">Public Label</label>
                          <input type="text" v-model="editingOption.newText" required class="premium-input" />
                      </div>
                    </div>
                  </div>
                  <div class="bg-slate-50 p-8 flex flex-col sm:flex-row-reverse gap-4">
                    <button type="submit" :disabled="isProcessing" class="bg-[#20FCF1] text-[#0A1630] px-10 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl">
                      {{ isProcessing ? 'Synchronising...' : 'Update Entry' }}
                    </button>
                    <button type="button" class="text-[10px] font-black text-slate-400 uppercase tracking-widest" @click="closeModal">Cancel</button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup>
import { ref, computed, watch, reactive } from 'vue';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '@/composables/useAuth';
import { useUserStore } from '@/stores/userStore';
import { PencilSquareIcon, TrashIcon, ArrowUturnLeftIcon, ShieldCheckIcon } from '@heroicons/vue/24/outline';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';

const { claims } = useAuth();
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');
const manageFormOptions = httpsCallable(functions, 'manageFormOptions');

const loading = ref(true);
const error = ref(null);
const isProcessing = ref(false);
const rawOptionsData = ref({});
const isEditModalOpen = ref(false);

const newOptions = ref({});
const editingOption = reactive({
  sectionId: null,
  current: { text: '', value: '' },
  newText: ''
});

const formSections = ref([
  { 
    title: '1. Fatigue Management Protocol', 
    optionGroupIds: [
      'fatiguePlanReasonOptions',
      'fatiguePlanCauseOptions',
      'fatiguePlanImpactOptions',
      'fatiguePlanActionOptions'
    ] 
  },
  { 
    title: '2. Clinical Fatigue Assessment', 
    optionGroupIds: [
      'fatigueAssessmentReasonOptions'
    ] 
  }
]);

const formatTitle = (id) => {
    const map = {
        'fatiguePlanReasonOptions': 'Exceedance Triggers',
        'fatiguePlanCauseOptions': 'Contributing Drivers',
        'fatiguePlanImpactOptions': 'Risk Impacts',
        'fatiguePlanActionOptions': 'Mitigation Strategies',
        'fatigueAssessmentReasonOptions': 'Assessment Triggers'
    };
    return map[id] || id.replace(/([A-Z])/g, " $1").toUpperCase();
};

const allGroupIds = formSections.value.flatMap(s => s.optionGroupIds);
allGroupIds.forEach(id => newOptions.value[id] = { value: '', text: '' });

const clientHasOverrides = computed(() => {
  const result = {};
  if (!claims.value?.clientId) return result;
  allGroupIds.forEach(id => {
    result[id] = rawOptionsData.value[id] && rawOptionsData.value[id][claims.value.clientId] !== undefined;
  });
  return result;
});

const displayOptions = computed(() => {
  const result = {};
  const clientId = claims.value?.clientId;
  allGroupIds.forEach(id => {
    const sectionData = rawOptionsData.value[id];
    if (sectionData) {
      result[id] = (clientId && sectionData[clientId]) ? sectionData[clientId] : (sectionData.global || []);
    } else {
      result[id] = [];
    }
  });
  return result;
});

const fetchFormOptions = async () => {
  if (!claims.value?.clientId) return;
  loading.value = true;
  try {
    const docSnaps = await Promise.all(allGroupIds.map(id => getDoc(doc(db, 'formOptions', id))));
    const newOptionsData = {};
    docSnaps.forEach((docSnap, index) => {
      const sectionId = allGroupIds[index];
      newOptionsData[sectionId] = docSnap.exists() ? docSnap.data() : { global: [] };
    });
    rawOptionsData.value = newOptionsData;
  } catch (err) {
    error.value = "Failed to load protocol options.";
  } finally {
    loading.value = false;
  }
};

watch(() => claims.value, (newClaims) => {
  if (newClaims?.clientId) fetchFormOptions();
}, { immediate: true });

const getSanitizedList = (sectionId) => {
    const currentList = clientHasOverrides.value[sectionId] 
        ? rawOptionsData.value[sectionId][claims.value.clientId]
        : displayOptions.value[sectionId];
    return JSON.parse(JSON.stringify(currentList || []));
};

const handleAddOption = async (sectionId) => {
  const input = newOptions.value[sectionId];
  if (!input.value || !input.text) return;
  isProcessing.value = true;
  const payload = { value: input.value.trim().toLowerCase().replace(/\s+/g, '_'), text: input.text.trim() };
  try {
    if (userStore.isAdmin) {
        const currentList = getSanitizedList(sectionId);
        currentList.push(payload);
        await setDoc(doc(db, 'formOptions', sectionId), { [claims.value.clientId]: currentList }, { merge: true });
    } else {
        await manageFormOptions({ action: 'add', sectionId, clientId: claims.value.clientId, option: payload });
    }
    newOptions.value[sectionId] = { value: '', text: '' };
    await fetchFormOptions();
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const handleUpdateOption = async () => {
  const { sectionId, current, newText } = editingOption;
  const updatedOption = { text: newText.trim(), value: current.value };
  isProcessing.value = true;
  try {
    if (userStore.isAdmin) {
        const currentList = getSanitizedList(sectionId);
        const index = currentList.findIndex(opt => opt.value === current.value);
        if (index !== -1) currentList[index] = updatedOption;
        await setDoc(doc(db, 'formOptions', sectionId), { [claims.value.clientId]: currentList }, { merge: true });
    } else {
        await manageFormOptions({ action: 'remove', sectionId, clientId: claims.value.clientId, option: current });
        await manageFormOptions({ action: 'add', sectionId, clientId: claims.value.clientId, option: updatedOption });
    }
    await fetchFormOptions();
    isEditModalOpen.value = false;
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const handleDeleteOption = async (sectionId, optionToRemove) => {
  if (!confirm('Authorise removal of this option for your client?')) return;
  isProcessing.value = true;
  try {
    if (userStore.isAdmin) {
        const currentList = getSanitizedList(sectionId);
        const newList = currentList.filter(opt => opt.value !== optionToRemove.value);
        await setDoc(doc(db, 'formOptions', sectionId), { [claims.value.clientId]: newList }, { merge: true });
    } else {
        await manageFormOptions({ action: 'remove', sectionId, clientId: claims.value.clientId, option: optionToRemove });
    }
    await fetchFormOptions();
  } catch (err) {
    alert(`Error: ${err.message}`);
  } finally {
    isProcessing.value = false;
  }
};

const revertToGlobal = async (sectionId) => {
    if (!confirm('Reset to Global defaults? This will erase client customisations.')) return;
    isProcessing.value = true;
    try {
        await manageFormOptions({ action: 'reset', sectionId, clientId: claims.value.clientId });
        await fetchFormOptions();
    } catch (err) {
        alert(`Error: ${err.message}`);
    } finally {
        isProcessing.value = false;
    }
};

const openEditModal = (sectionId, option) => {
    editingOption.sectionId = sectionId;
    editingOption.current = option;
    editingOption.newText = option.text;
    isEditModalOpen.value = true;
};
const closeModal = () => { isEditModalOpen.value = false; };
</script>

<style scoped>
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]; }
.premium-input { @apply mt-1 block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-[#0A1630] shadow-sm focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.premium-dark-input { @apply block w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm font-bold text-white shadow-inner focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
</style>